import {
  useState,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext,
  type ReactNode
} from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Radio,
  Select,
  Show
} from "shared/components";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { cx } from "shared/utils";
import { components, MenuListProps } from "react-select";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { SectionCard } from "shared/app/SectionCard";
import { Unit, UnitAccessMethod } from "types/landlord/address";
import addressApi from "api/landlord/address";
import { lockboxesApi } from "api/landlord/lockboxes";
import { VerificationStatusPill } from "views/lockboxes/components/VerificationStatusPill";
import {
  AUTO_GENERATED_CODE_LABEL,
  CODE_TO_SEND_UNSET_LABEL,
  codeToSendSlotLabel,
  isNoVendor
} from "views/lockboxes/constants";
import { getErrorMessage } from "utils/error";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { LockboxCodeGenerator } from "shared/app/LockboxCodeGenerator";
import { HugeiconsIcon } from "@hugeicons/react";
import { Alert02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { unitsApi, UNITS_NEEDS_LOCKBOX_COUNT_KEY } from "api/units";
import { invalidateNeedsLockbox } from "shared/utils/units-cache";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

type LockboxOption = { value: number; label: string };

const lockboxLinkedMessage = (remaining: number) => {
  if (remaining === 0) return "Lockbox linked. No units need a lockbox.";
  if (remaining === 1) return "Lockbox linked. 1 unit still needs a lockbox.";
  return `Lockbox linked. ${remaining} units still need a lockbox.`;
};

const noLockboxHelp = (unit: string) =>
  `Leads get this ${unit}'s entry instructions instead of a lockbox code. Put the lockbox or door code in the entry instructions.`;

const NOTE_TONES = {
  warning: {
    box: "border-warning-100 bg-warning-50 text-warning-800",
    icon: "text-warning-600"
  },
  danger: {
    box: "border-danger-100 bg-danger-50 text-danger-700",
    icon: "text-danger-700"
  }
};

const accessErrorMessages = (unit: string): Record<string, string> => ({
  entry_instructions_missing: `This ${unit} has no entry instructions. Add them first so leads know how to get in.`,
  not_self_guided:
    "Access can only be changed when the listing template uses self-guided showings."
});

const accessErrorMessage = (error: unknown, unit: string) => {
  const message = getErrorMessage(error);
  return accessErrorMessages(unit)[message] ?? message;
};

const hasEntryInstructions = (unit: Unit) =>
  Boolean(unit.instructions?.entry?.text?.trim());

const AccessNote = ({
  tone,
  children
}: {
  tone: keyof typeof NOTE_TONES;
  children: ReactNode;
}) => (
  <div
    role={tone === "danger" ? "alert" : "status"}
    className={cx(
      "flex gap-2.5 rounded-lg border px-3.5 py-3 text-sm",
      NOTE_TONES[tone].box
    )}
  >
    <HugeiconsIcon
      icon={Alert02Icon}
      size={18}
      className={cx("shrink-0", NOTE_TONES[tone].icon)}
    />
    <span>{children}</span>
  </div>
);

const AddEntryInstructionsLink = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant={ButtonVariant.TEXT}
    className="text-sm underline"
    onClick={onClick}
  >
    Add entry instructions
  </Button>
);

// ─── Context ──────────────────────────────────────────────────────────────────

interface LockboxMenuContextValue {
  propertyOptions: LockboxOption[];
  unassignedOptions: LockboxOption[];
  selectedLockboxId: number | null;
  isLoading: boolean;
  onSelect: (opt: LockboxOption) => void;
}

const LockboxMenuContext = createContext<LockboxMenuContextValue | null>(null);

const useLockboxMenuContext = () => {
  const ctx = useContext(LockboxMenuContext);
  if (!ctx) throw new Error("Must be inside LockboxMenuContext.Provider");
  return ctx;
};

// ─── Custom MenuList (module-level, stable reference) ─────────────────────────

const SectionHeader = ({
  title,
  className = "pt-1"
}: {
  title: string;
  className?: string;
}) => (
  <div className={`px-4 ${className} pb-2`}>
    <span className="text-xs text-content-200">{title}</span>
    <div className="border-b border-alpha-40 mt-2" />
  </div>
);

const LockboxMenuItemRow = ({
  opt,
  isCurrent,
  onSelect
}: {
  opt: LockboxOption;
  isCurrent: boolean;
  onSelect: (opt: LockboxOption) => void;
}) => (
  <button
    type="button"
    className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left cursor-pointer hover:bg-alpha-40"
    onClick={() => onSelect(opt)}
  >
    <span className="text-sm font-medium text-content-1000 truncate min-w-0">
      {opt.label}
    </span>
    {isCurrent && (
      <Badge
        variant={BadgeVariant.PRIMARY}
        size={BadgeSize.SM}
        className="shrink-0"
      >
        Current
      </Badge>
    )}
  </button>
);

const CustomMenuList = (props: MenuListProps<unknown>) => {
  const {
    propertyOptions,
    unassignedOptions,
    selectedLockboxId,
    isLoading,
    onSelect
  } = useLockboxMenuContext();

  const hasBothGroups =
    propertyOptions.length > 0 && unassignedOptions.length > 0;

  if (isLoading)
    return (
      <components.MenuList {...props}>
        <div className="px-4 py-2 text-center text-content-500">Loading...</div>
      </components.MenuList>
    );

  if (propertyOptions.length === 0 && unassignedOptions.length === 0)
    return (
      <components.MenuList {...props}>
        <div className="px-4 py-2 text-center text-content-500">
          No lockboxes found
        </div>
      </components.MenuList>
    );

  return (
    <components.MenuList {...props}>
      <div className="bg-background-1000 py-1">
        {propertyOptions.length > 0 && (
          <>
            {hasBothGroups && (
              <SectionHeader title="Assigned to this property" />
            )}
            {propertyOptions.map((opt) => (
              <LockboxMenuItemRow
                key={opt.value}
                opt={opt}
                isCurrent={opt.value === selectedLockboxId}
                onSelect={onSelect}
              />
            ))}
          </>
        )}
        {unassignedOptions.length > 0 && (
          <>
            <SectionHeader
              title="Unassigned"
              className={hasBothGroups ? "pt-3" : "pt-1"}
            />
            {unassignedOptions.map((opt) => (
              <LockboxMenuItemRow
                key={opt.value}
                opt={opt}
                isCurrent={opt.value === selectedLockboxId}
                onSelect={onSelect}
              />
            ))}
          </>
        )}
      </div>
    </components.MenuList>
  );
};

const lockboxSelectorComponents = { MenuList: CustomMenuList };

// ─── EditLockboxForm ──────────────────────────────────────────────────────────

const EditLockboxForm = ({
  unit,
  propertyId,
  onClose,
  onAddEntryInstructions
}: {
  unit: Unit;
  propertyId: number;
  onClose: () => void;
  onAddEntryInstructions: () => void;
}) => {
  const selectRef = useRef<{ blur: () => void } | null>(null);
  const dictionary = useUnitsPropertiesDictionary();
  const [accessMethod, setAccessMethod] = useState<UnitAccessMethod>(
    unit.access_method ?? "lockbox"
  );
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [selectedLockboxId, setSelectedLockboxId] = useState<number | null>(
    unit.lockbox?.id ?? null
  );
  const [selectedOption, setSelectedOption] = useState<LockboxOption | null>(
    unit.lockbox ? { value: unit.lockbox.id, label: unit.lockbox.name } : null
  );

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const queryClient = useQueryClient();

  const { data: availableLockboxes = [], isLoading } = useQuery({
    queryKey: [
      "LOCKBOXES",
      {
        available: true,
        property_id: propertyId,
        allow_no_property: true,
        search: debouncedSearch
      }
    ],
    queryFn: () =>
      lockboxesApi
        .getLockboxes({
          available: true,
          property_id: propertyId,
          allow_no_property: true,
          per_page: 100,
          search: debouncedSearch || undefined
        })
        .then((r) => r.items)
  });

  const propertyOptions = useMemo(() => {
    const fromQuery = availableLockboxes
      .filter((lb) => lb.property?.id === propertyId)
      .map((lb) => ({ value: lb.id, label: lb.name }));

    // currently-assigned lockbox has unit_id set → excluded by available: true, inject manually
    if (unit.lockbox && !fromQuery.find((o) => o.value === unit.lockbox!.id))
      return [
        { value: unit.lockbox.id, label: unit.lockbox.name },
        ...fromQuery
      ];

    return fromQuery;
  }, [availableLockboxes, propertyId, unit.lockbox]);

  const unassignedOptions = useMemo(
    () =>
      availableLockboxes
        .filter((lb) => !lb.property)
        .map((lb) => ({ value: lb.id, label: lb.name })),
    [availableLockboxes]
  );

  const allOptions = useMemo(() => {
    const available = [...propertyOptions, ...unassignedOptions];
    if (
      selectedOption &&
      !available.find((o) => o.value === selectedOption.value)
    )
      return [selectedOption, ...available];
    return available;
  }, [propertyOptions, unassignedOptions, selectedOption]);

  const selectedLockboxCode = useMemo(
    () =>
      availableLockboxes.find((lb) => lb.id === selectedLockboxId)?.code ??
      (unit.lockbox?.id === selectedLockboxId
        ? (unit.lockbox?.code ?? null)
        : null),
    [availableLockboxes, selectedLockboxId, unit.lockbox]
  );

  const handleSelect = useCallback((opt: LockboxOption) => {
    setSelectedLockboxId(opt.value);
    setSelectedOption(opt);
    selectRef.current?.blur();
  }, []);

  const contextValue = useMemo<LockboxMenuContextValue>(
    () => ({
      propertyOptions,
      unassignedOptions,
      selectedLockboxId,
      isLoading,
      onSelect: handleSelect
    }),
    [
      propertyOptions,
      unassignedOptions,
      selectedLockboxId,
      isLoading,
      handleSelect
    ]
  );

  const isNoLockbox = accessMethod === "entry_instructions";
  const missingEntryInstructions = isNoLockbox && !hasEntryInstructions(unit);

  const { mutate: save, isPending } = useMutation({
    mutationKey: ["UNIT", "UPDATE_LOCKBOX"],
    mutationFn: () =>
      addressApi.updateUnitAccess(
        unit.id,
        isNoLockbox
          ? { access_method: "entry_instructions" }
          : { access_method: "lockbox", lockbox_id: selectedLockboxId }
      ),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["unit", String(propertyId), String(unit.id)]
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      invalidateNeedsLockbox(queryClient);
      setConfirmingUnlink(false);
      onClose();

      if (isNoLockbox) {
        toast.success("Access updated. Leads will get the entry instructions.");
        return;
      }

      // The fresh count also feeds the Units menu badge and banner.
      const remaining = await queryClient
        .fetchQuery({
          queryKey: [UNITS_NEEDS_LOCKBOX_COUNT_KEY],
          queryFn: unitsApi.getNeedsLockboxCount,
          staleTime: 0
        })
        .then(({ count }) => count)
        .catch(() => null);

      toast.success(
        selectedLockboxId && remaining !== null
          ? lockboxLinkedMessage(remaining)
          : "Lockbox updated successfully"
      );
    },
    onError: (err) => {
      toast.error(accessErrorMessage(err, dictionary.unit));
    }
  });

  const handleSave = () => {
    if (isNoLockbox && unit.lockbox) {
      setConfirmingUnlink(true);
      return;
    }
    save();
  };

  return (
    <SectionCard title="Lockbox">
      <div className="space-y-4">
        <div className="space-y-3">
          <p className="text-sm font-bold text-content-1000">Access</p>
          <Radio
            id={`unit_access_lockbox_${unit.id}`}
            name={`unit_access_${unit.id}`}
            value="lockbox"
            checked={!isNoLockbox}
            disabled={isPending}
            onChange={() => setAccessMethod("lockbox")}
            label={
              <span className="text-sm font-medium text-content-1000">
                Use a lockbox
              </span>
            }
          />
          <div className="space-y-1">
            <Radio
              id={`unit_access_entry_instructions_${unit.id}`}
              name={`unit_access_${unit.id}`}
              value="entry_instructions"
              checked={isNoLockbox}
              disabled={isPending}
              onChange={() => setAccessMethod("entry_instructions")}
              label={
                <span className="text-sm font-medium text-content-1000">
                  No lockbox, access details are in the entry instructions
                </span>
              }
            />
            <p className="pl-6 text-sm font-medium text-content-300">
              {noLockboxHelp(dictionary.unit)}
            </p>
          </div>
        </div>
        <Show if={missingEntryInstructions}>
          <AccessNote tone="danger">
            This {dictionary.unit} has no entry instructions. Add them first so
            leads know how to get in.{" "}
            <AddEntryInstructionsLink onClick={onAddEntryInstructions} />
          </AccessNote>
        </Show>
        <Show if={!isNoLockbox}>
          <div className="flex gap-4">
            <div className="flex-1">
              <LockboxMenuContext.Provider value={contextValue}>
                <Select
                  ref={selectRef}
                  id="lockbox"
                  label="Lockbox"
                  value={selectedLockboxId ?? ""}
                  clearable
                  hideOptional
                  searchable
                  options={allOptions}
                  placeholder="Select lockbox"
                  components={lockboxSelectorComponents}
                  loading={isLoading}
                  onInputChange={(val: string) => setSearchInput(val)}
                  onChange={(e) => {
                    if (!Array.isArray(e) && !e.target.value) {
                      setSelectedLockboxId(null);
                      setSelectedOption(null);
                    }
                  }}
                />
              </LockboxMenuContext.Provider>
            </div>
            <BaseInput
              id="admin_code"
              label="Admin code"
              className="flex-1"
              value={selectedLockboxCode ?? ""}
              onChange={() => {}}
              disabled
              hideOptional
            />
          </div>
        </Show>
        <SaveCancelButtons
          onSave={handleSave}
          onCancel={onClose}
          saveLoading={isPending}
          saveDisabled={isPending || missingEntryInstructions}
        />
      </div>
      <ConfirmDialog
        opened={confirmingUnlink}
        onClose={() => setConfirmingUnlink(false)}
        onConfirm={() => save()}
        title={`Unlink ${unit.lockbox?.name ?? "this lockbox"}?`}
        description={
          <div className="space-y-2">
            <p>
              Leads will get this {dictionary.unit}&apos;s entry instructions
              instead of a lockbox code.
            </p>
            <p>
              The lockbox stays on your Lockboxes page. Codes already sent to
              leads keep working until they expire.
            </p>
          </div>
        }
        variant="primary"
        icon={{ icon: Alert02Icon, variant: "warning" }}
        confirmButton={{ label: "Unlink and save", loading: isPending }}
        cancelButton={{ label: "Cancel", disabled: isPending }}
      />
    </SectionCard>
  );
};

const codeToSendValue = (lockbox: NonNullable<Unit["lockbox"]>) => {
  if (!lockbox.vendor) return "--";
  if (!isNoVendor(lockbox.vendor)) return AUTO_GENERATED_CODE_LABEL;
  if (!lockbox.code_to_send_slot || !lockbox.code_to_send)
    return CODE_TO_SEND_UNSET_LABEL;
  return `${codeToSendSlotLabel(lockbox.code_to_send_slot)} · ${lockbox.code_to_send}`;
};

// ─── LockboxSection ───────────────────────────────────────────────────────────

export const LockboxSection = ({
  unit,
  propertyId,
  propertyTimezone,
  onAddEntryInstructions,
  onShowEntryInstructions
}: {
  unit: Unit;
  propertyId: number;
  propertyTimezone?: string | null;
  onAddEntryInstructions?: () => void;
  onShowEntryInstructions?: () => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const goToUnitPage = () =>
    navigate(`/properties/${propertyId}/units/${unit.id}`);
  const addEntryInstructions = onAddEntryInstructions ?? goToUnitPage;
  const showEntryInstructions = onShowEntryInstructions ?? goToUnitPage;
  const { type, partner } = useAuthContext();
  const dictionary = useUnitsPropertiesDictionary();

  const lockbox = unit.lockbox;

  const vendorConnected =
    (lockbox?.vendor === "sentrilock" && !!partner?.sentrilock_connected) ||
    (lockbox?.vendor === "codebox" && !!partner?.codebox_connected) ||
    (lockbox?.vendor === "igloohome" && !!partner?.igloohome_connected) ||
    (lockbox?.vendor === "rently" && !!partner?.rently_lockboxes_connected);

  const canGenerateCode =
    !!lockbox && type.isBoomCRM && type.isLockboxes && vendorConnected;

  if (isEditing)
    return (
      <EditLockboxForm
        unit={unit}
        propertyId={propertyId}
        onClose={() => setIsEditing(false)}
        onAddEntryInstructions={addEntryInstructions}
      />
    );

  const editButton = (
    <Button
      size={ButtonSize.SM}
      variant={ButtonVariant.BASE}
      onClick={() => setIsEditing(true)}
    >
      Edit
    </Button>
  );

  if (unit.access_method === "entry_instructions")
    return (
      <SectionCard title="Lockbox" headerExtra={editButton}>
        <div className="space-y-4">
          <Show if={!hasEntryInstructions(unit)}>
            <AccessNote tone="warning">
              This {dictionary.unit} has no entry instructions. Leads who book a
              self-guided showing won&apos;t receive any access details.{" "}
              <AddEntryInstructionsLink onClick={addEntryInstructions} />
            </AccessNote>
          </Show>
          <Row
            title="Access"
            value={
              <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
                No lockbox
              </Badge>
            }
            growValueWidth
          />
          <Row
            title="Lead receives"
            value={
              <Button
                variant={ButtonVariant.TEXT}
                className="text-sm underline"
                onClick={showEntryInstructions}
              >
                Entry instructions
              </Button>
            }
            growValueWidth
          />
        </div>
      </SectionCard>
    );

  return (
    <SectionCard title="Lockbox" headerExtra={editButton}>
      <div className="space-y-4">
        <Show if={Boolean(unit.needs_lockbox)}>
          <AccessNote tone="warning">
            No lockbox is linked. Leads who book a self-guided showing
            won&apos;t receive an access code.
          </AccessNote>
        </Show>
        <Row title="Lockbox" value={lockbox?.name ?? "--"} growValueWidth />
        {lockbox && (
          <Row
            title="Verification"
            value={
              <VerificationStatusPill
                status={lockbox.verification_status}
                vendor={lockbox.vendor}
              />
            }
            growValueWidth
          />
        )}
        <Row title="Admin code" value={lockbox?.code ?? "--"} growValueWidth />
        {lockbox && (
          <Row
            title="Code to send"
            value={codeToSendValue(lockbox)}
            growValueWidth
          />
        )}

        {canGenerateCode && lockbox && (
          <LockboxCodeGenerator
            key={`${lockbox.id}:${lockbox.vendor}`}
            lockbox={lockbox}
            timezone={propertyTimezone}
            assignAccountHint="on the Lockboxes page"
          />
        )}
      </div>
    </SectionCard>
  );
};
